import { z } from 'zod';

const num = z.coerce.number();
const numNull = z.coerce.number().nullable();

// ── Bundle offline (GET /imoveis/:id/visita-bundle) ──────────────────────
export const bundleTalhaoSchema = z.object({
  talhao_id: num,
  // Fazenda do talhão — na visita multi-fazenda os bundles se misturam e é isso
  // que diz de qual fazenda o ponto é. Optional: backend antigo/cache velho.
  imovel_id: numNull.optional(),
  numero: z.string(),
  // Nome do talhão (KML/edição) — optional pra tolerar backend antigo/cache velho.
  nome: z.string().nullable().optional(),
  area_ha: numNull,
  geojson: z.unknown().nullable(),
  safra: z.string().nullable(),
  cultura: z.string().nullable(),
  variedade: z.string().nullable(),
  data_plantio: z.string().nullable(),
  data_emergencia: z.string().nullable(),
  ciclo_dias: numNull,
  ultimo_estadio: z.string().nullable(),
});
export type BundleTalhao = z.infer<typeof bundleTalhaoSchema>;

export const fazendaHeaderSchema = z.object({
  imovel_id: num,
  fazenda: z.string().nullable(),
  produtor: z.string().nullable(),
  proprietario_cpf: z.string().nullable(),
  municipio: z.string().nullable(),
  uf: z.string().nullable(),
});

// ── Catálogo MIP (itens + compostos) ─────────────────────────────────────
export const mipItemSchema = z.object({
  id: num,
  categoria: z.string(),
  nome: z.string(),
  nome_cientifico: z.string().nullable(),
  unidade: z.string(),
  fases: z.array(z.string()).default([]),
  limiar_atencao: numNull,
  limiar_acao: numNull,
});
export type MipItem = z.infer<typeof mipItemSchema>;

export const mipCompostoSchema = z.object({
  id: num,
  nome: z.string(),
  fase: z.string().nullable(),
  item_ids: z.array(num).default([]),
  limiar_acao: numNull,
  unidade: z.string().nullable(),
});
export type MipComposto = z.infer<typeof mipCompostoSchema>;

export const mipCatalogoSchema = z.object({
  itens: z.array(mipItemSchema).default([]),
  compostos: z.array(mipCompostoSchema).default([]),
});
export type MipCatalogo = z.infer<typeof mipCatalogoSchema>;

export const visitaBundleSchema = z.object({
  fazenda: fazendaHeaderSchema.nullable(),
  talhoes: z.array(bundleTalhaoSchema),
  catalogo: mipCatalogoSchema.optional(),
  gerado_em: z.string(),
});
export type VisitaBundle = z.infer<typeof visitaBundleSchema>;

// ── Indicadores MIP (computados no servidor) ─────────────────────────────
export const indicadorItemSchema = z.object({
  mip_item_id: numNull,
  nome: z.string(),
  categoria: z.string(),
  unidade: z.string().nullable(),
  media_adultos: numNull,
  media_ninfas: numNull,
  media_total: numNull,
  pct_com_ovos: numNull,
  severidade: z.string(),
});
export const indicadoresSchema = z.object({
  n_amostras: num,
  por_item: z.array(indicadorItemSchema).default([]),
  compostos: z.array(z.object({ nome: z.string(), fase: z.string().nullable(), unidade: z.string().nullable(), media: numNull, severidade: z.string() })).default([]),
  categorias: z.record(z.coerce.number()).default({}),
  severidade_geral: z.string(),
  apontamentos: z.array(z.object({ nome: z.string(), severidade: z.string(), detalhe: z.string() })).default([]),
});
export type Indicadores = z.infer<typeof indicadoresSchema>;

// ── Resposta do sync (POST /visitas) ─────────────────────────────────────
export const syncResponseSchema = z.object({
  ok: z.boolean(),
  visita_id: num.nullable(),
  fotos_inseridas: num,
  fotos_duplicadas: num,
  fotos_sem_arquivo: num,
  fotos_nao_resolvidas: num,
  amostras_inseridas: num.default(0),
  talhoes: z.record(z.coerce.number()).default({}),
});
export type SyncResponse = z.infer<typeof syncResponseSchema>;

// ── Detalhe da sessão (GET /visitas/:id) ─────────────────────────────────
// Um áudio da foto (tabela relatorio_foto_audio). São vários por foto.
export const visitaAudioSchema = z.object({
  id: num,
  url: z.string(),
  dur_ms: numNull,
  transcricao: z.string().nullable(),
  status: z.string().nullable(),
});
export type VisitaAudio = z.infer<typeof visitaAudioSchema>;

export const visitaFotoSchema = z.object({
  id: num,
  // Ponto marcado sem foto: arquivo e url vêm nulos.
  arquivo: z.string().nullable(),
  legenda: z.string().nullable(),
  lat: numNull,
  lon: numNull,
  capturado_em: z.string().nullable(),
  recomendacao: z.string().nullable(),
  estadio_fenologico: z.string().nullable(),
  dae: numNull,
  ponto_uuid: z.string().nullable().optional(),
  ia_status: z.string().nullable(),
  ia_analise: z.unknown().nullable(),
  url: z.string().nullable(),
  audios: z.array(visitaAudioSchema).default([]),
  audio_url: z.string().nullable().optional(),
  audio_transcricao: z.string().nullable().optional(),
  audio_status: z.string().nullable().optional(),
});

export const visitaTalhaoSchema = z.object({
  id: num,
  talhao_id: num,
  talhao_numero: z.string(),
  // Nome do talhão (KML/edição). O sistema mostra nome > "Talhão <numero>".
  talhao_nome: z.string().nullable().optional(),
  area_ha: numNull,
  estadio_fenologico: z.string().nullable(),
  variedade: z.string().nullable(),
  data_plantio: z.string().nullable(),
  cultura: z.string().nullable(),
  fotos: z.array(visitaFotoSchema),
  ocorrencias: z.array(
    z.object({
      id: num,
      tipo: z.string(),
      nome: z.string().nullable(),
      severidade: z.string().nullable(),
      quantidade: numNull,
      unidade: z.string().nullable(),
      observacao: z.string().nullable(),
      ponto_uuid: z.string().nullable().optional(),
    }),
  ),
  indicadores: indicadoresSchema.optional(),
});

export const visitaDetailSchema = z
  .object({
    id: num,
    imovel_id: numNull,
    data_visita: z.string(),
    agronomo_nome: z.string().nullable(),
    observacoes_gerais: z.string().nullable(),
    status: z.string().nullable(),
    fazenda: z.string().nullable(),
    produtor: z.string().nullable(),
    municipio: z.string().nullable(),
    uf: z.string().nullable(),
    resumo_ia: z.string().nullable().optional(),
    resumo_ia_em: z.string().nullable().optional(),
    talhoes: z.array(visitaTalhaoSchema),
  })
  .passthrough();
export type VisitaDetail = z.infer<typeof visitaDetailSchema>;
export type VisitaTalhao = z.infer<typeof visitaTalhaoSchema>;
export type IndicadorItem = z.infer<typeof indicadorItemSchema>;

// ── Lista de sessões da fazenda (GET /imoveis/:id/visitas) ───────────────
export const visitaListItemSchema = z.object({
  id: num,
  data_visita: z.string(),
  agronomo_nome: z.string().nullable(),
  observacoes_gerais: z.string().nullable(),
  status: z.string().nullable(),
  origem: z.string().nullable(),
  talhoes: num,
  fotos: num,
});
export const visitaListSchema = z.array(visitaListItemSchema);
export type VisitaListItem = z.infer<typeof visitaListItemSchema>;

// ── Análise da IA (gpt-4o) gravada em relatorio_foto.ia_analise ──────────
export const iaItemSchema = z.object({
  nome: z.string(),
  confianca: z.coerce.number().nullable().optional(),
  severidade: z.string().nullable().optional(),
  evidencia: z.string().nullable().optional(),
});
export type IaItem = z.infer<typeof iaItemSchema>;

export const iaAnaliseSchema = z.object({
  planta_identificada: z.string().nullable().optional(),
  e_da_cultura: z.boolean().optional(),
  resumo: z.string().nullable().optional(),
  doencas: z.array(iaItemSchema).default([]),
  pragas: z.array(iaItemSchema).default([]),
  deficiencias: z.array(iaItemSchema).default([]),
  recomendacao: z.string().nullable().optional(),
});
export type IaAnalise = z.infer<typeof iaAnaliseSchema>;

// Tipo de ocorrência por categoria da IA.
export const TIPO_POR_CATEGORIA: Record<'doencas' | 'pragas' | 'deficiencias', string> = {
  doencas: 'doenca',
  pragas: 'praga',
  deficiencias: 'nutricao',
};
